import { FC } from 'react';
import {
  PageContentLayout,
  PageHeaderLayout,
  Loader,
  NothingFoundAlert,
  SideMenu,
  ActionsMenu,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import { useTabsState } from 'utils/Tabs';
import { useRouteParams } from 'utils/useIdParam';
import { tabsSchema } from 'utils/schemas';
import { useHistory } from 'react-router-dom';
import {
  useAdminBenefitTemplate,
  useAdminCopyBenefitTemplate,
  useAdminDeleteBenefitTemplate,
} from 'store/admin-benefit-templates';
import { useConfirm } from 'store/confirm';
import { usePredefinedToasts } from 'utils/Toast';
import { BenefitTemplateContent, BenefitTemplateSettings } from './atoms';

const benefitTemplateDetailsTabsSchema = tabsSchema(['settings', 'content']);

export const AdminBenefitTemplateDetails: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id: benefitTemplateId } = useRouteParams(
    ROUTES.admin.benefitTemplates.details,
  );
  const { confirmation } = useConfirm();
  const predefinedToasts = usePredefinedToasts();

  const sideTabs = useTabsState(
    [
      {
        key: 'settings',
        label: t('common.settings'),
      },
      {
        key: 'content',
        label: t('benefitTemplates.templateContent'),
      },
    ],
    benefitTemplateDetailsTabsSchema,
  );

  const { data: template, isLoading } =
    useAdminBenefitTemplate(benefitTemplateId);

  const { mutate: deleteTemplate, isLoading: isDeleting } =
    useAdminDeleteBenefitTemplate();
  const { mutate: copyTemplate, isLoading: isCopying } =
    useAdminCopyBenefitTemplate();

  if (isLoading) return <Loader />;
  if (!template) return <NothingFoundAlert />;

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('menu.templates'),
            url: ROUTES.admin.benefitTemplates.list,
          },
          {
            label: template.name,
          },
        ]}
        side={
          <ActionsMenu
            items={[
              {
                label: t('actions.make_copy'),
                onClick: () => {
                  copyTemplate(
                    { benefitTemplateId: template.id },
                    {
                      onSuccess: ({ data }) => {
                        predefinedToasts.success.added();
                        history.push(
                          ROUTES.admin.benefitTemplates.details.replace(
                            ':id',
                            data.id,
                          ),
                        );
                      },
                    },
                  );
                },
                disabled: isCopying,
              },
              {
                label: t('actions.delete'),
                onClick: async () => {
                  const { confirmed } = await confirmation(
                    t('common.areYouSureToDelete', {
                      title: template.name,
                    }),
                    undefined,
                    'danger',
                  );

                  if (confirmed) {
                    deleteTemplate(
                      { benefitTemplateId: template.id },
                      {
                        onSuccess: () => {
                          predefinedToasts.success.deleted();
                          history.push(ROUTES.admin.benefitTemplates.list);
                        },
                      },
                    );
                  }
                },
                color: 'error',
                disabled: isDeleting,
              },
            ]}
          />
        }
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            activeTab={sideTabs.activeTab}
            onTabChange={sideTabs.goTo}
            tabs={sideTabs.tabs}
          />
        }
      >
        {sideTabs.activeTab === 'settings' && <BenefitTemplateSettings />}
        {sideTabs.activeTab === 'content' && <BenefitTemplateContent />}
      </PageContentLayout>
    </Stack>
  );
};
